import { useCallback, useRef } from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { functionUnionWithCondition } from '@atlaskit/platform-feature-flags-react';

import { invite as inviteService } from '@atlassian/expansion-services/services';
import { showInviteFlags } from '@atlassian/invite-on-third-party-link';
import { fetchEligibleEmails } from '@atlassian/invite-on-third-party-link/services/third-party-link/fetch-eligible-emails';
import { fetchPiSlackLinkEligibleUsers } from '@atlassian/invite-on-third-party-link/services/third-party-link/fetch-pi-slack-link-eligible-users';
import type { FetchEligibleEmailsResponse } from '@atlassian/invite-on-third-party-link/services/third-party-link/types';

import { usePageSpaceKey } from '@confluence/page-context';
import { useSpaceDetail } from '@confluence/space-utils/entry-points/useSpaceDetail.ts';
import { useService } from '@confluence/expansion-services';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import {
	getAllLinks,
	getChannelIdFromSlackLink,
	isMailtoLink,
	isSlackLink,
	getEmailsFromMailtoLink,
} from './utils/links.ts';

// Gated hook wrappers — defined at module level so React's rules of hooks are
// satisfied (hook call order is stable) while avoiding the work when the gate is off.
const useInviteServiceGated = functionUnionWithCondition(
	() => fg('twcg-853-3p-in-1p-link-gate'),
	(service: typeof inviteService) => useService(service),
	() => null,
);

const usePageSpaceKeyGated = functionUnionWithCondition(
	() => fg('twcg-853-3p-in-1p-link-gate'),
	usePageSpaceKey,
	() => [undefined] as const,
);

const useSpaceDetailGated = functionUnionWithCondition(
	() => fg('twcg-853-3p-in-1p-link-gate'),
	useSpaceDetail,
	() => ({ data: undefined, loading: false }),
);

type PiEligibilityResult = Awaited<ReturnType<typeof fetchPiSlackLinkEligibleUsers>>;

/**
 * Classify a list of URLs into slack/mailto buckets, excluding any that appear
 * in `startingLinks`.
 */
const classifyNewLinks = (
	urls: Iterable<string>,
	startingLinks: Set<string>,
): { pastedSlackLinks: string[]; pastedMailtoLinks: string[] } => {
	const pastedSlackLinks: string[] = [];
	const pastedMailtoLinks: string[] = [];

	for (const link of urls) {
		if (startingLinks.has(link)) {
			continue;
		}
		if (isSlackLink(link)) {
			pastedSlackLinks.push(link);
		} else if (isMailtoLink(link)) {
			pastedMailtoLinks.push(link);
		}
	}

	return { pastedSlackLinks, pastedMailtoLinks };
};

export const useDetectPastedThirdPartyLinks = (flags?: FlagsStateContainer) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { userId, orgId, cloudId } = useSessionData();
	const [spaceKey] = usePageSpaceKeyGated();
	const { data: spaceDetail } = useSpaceDetailGated(spaceKey);
	const invite = useInviteServiceGated(inviteService);

	// Track what URLs existed when editor opened (initial state)
	// This enables URL-level delta detection
	const initialLinksRef = useRef<Set<string>>(new Set());

	/**
	 * Set the initial content when editor opens
	 */
	const setInitialLinks = useCallback((adfValue: ADFEntity | null | undefined) => {
		if (!fg('twcg-853-3p-in-1p-aa-test-gate') || !adfValue) {
			return;
		}
		const initialLinks = getAllLinks(adfValue);
		initialLinksRef.current = initialLinks;
	}, []);

	const fireAnalytics = useCallback(
		({
			pastedSlackLinks,
			pastedMailtoLinks,
		}: {
			pastedSlackLinks: string[];
			pastedMailtoLinks: string[];
		}) => {
			// 3P in 1P exp eligibility check flow
			if (
				(pastedSlackLinks.length === 0 && pastedMailtoLinks.length === 0) ||
				!fg('twcg-853-3p-in-1p-aa-test-gate')
			) {
				return;
			}

			// Exposure fired here
			const twcg3pIn1pAATestCohort = expValEquals('twcg-853-3p-in-1p-aa-test', 'isEnabled', true)
				? 'variation'
				: 'control';

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'pasted',
					actionSubject: '3PLink',
					actionSubjectId: 'editorPaste',
					source: 'inviteOnLinkInsert',
					attributes: {
						slackLinks: pastedSlackLinks,
						channelIds: pastedSlackLinks.map(getChannelIdFromSlackLink),
						mailtoLinkCount: pastedMailtoLinks.length,
						orgId,
						accountId: userId,
						twcg3pIn1pAATestCohort,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, userId, orgId],
	);

	const triggerSpotlight = useCallback(
		async (pastedSlackLinks: string[], pastedMailtoLinks: string[]) => {
			if (!fg('twcg-853-3p-in-1p-link-gate')) {
				return;
			}

			const hasThirdPartyInviteCandidates =
				pastedSlackLinks.length > 0 || pastedMailtoLinks.length > 0;
			if (hasThirdPartyInviteCandidates && flags && invite && fg('twcg-853-3p-in-1p-link-gate')) {
				const slackEligibilityUrls = Array.from(new Set(pastedSlackLinks));

				const pastedEmails = pastedMailtoLinks.flatMap(getEmailsFromMailtoLink);
				try {
					const [piSettled, emailSettled] = await Promise.allSettled([
						slackEligibilityUrls.length > 0
							? Promise.allSettled(
									slackEligibilityUrls.map((url) =>
										fetchPiSlackLinkEligibleUsers(url, 'confluence'),
									),
								)
							: Promise.resolve([]),
						pastedEmails.length > 0
							? fetchEligibleEmails(pastedEmails, 'confluence')
							: Promise.resolve({}),
					]);

					const piEligibilityResults: PiEligibilityResult[] =
						piSettled.status === 'fulfilled'
							? piSettled.value.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : []))
							: [];

					const eligibleEmailsResults: FetchEligibleEmailsResponse =
						emailSettled.status === 'fulfilled' ? emailSettled.value : {};

					const eligibleEmailCount = eligibleEmailsResults.eligibleEmails?.length ?? 0;
					const eligibleSlackCount = piEligibilityResults.reduce(
						(acc, result) => acc + (result?.eligibleUsers?.length ?? 0),
						0,
					);
					const hasInviteEligibleUsers = eligibleEmailCount + eligibleSlackCount > 0;
					if (
						hasInviteEligibleUsers &&
						expValEquals('twcg-853-3p-in-1p-link', 'cohort', 'variation')
					) {
						// Adapt Confluence FlagsStateContainer to the renderer contract expected by showInviteFlags.
						const showFlag = (args: {
							id: string;
							render: (props: { onDismiss: () => void }) => JSX.Element;
						}) => {
							void flags.showFlag({
								id: args.id,
								customComponent: () =>
									args.render({ onDismiss: () => void flags.hideFlag(args.id) }),
							});
						};
						const dismissFlag = (id: string) => {
							void flags.hideFlag(id);
						};

						showInviteFlags({
							cloudId,
							dismissFlag,
							eligibleEmailsResults,
							invite,
							piEligibilityResults,
							productName: 'Confluence',
							showFlag,
							spaceName: spaceDetail?.name ?? 'Confluence',
						});
					}
				} catch {
					// Invite flow failed — continue with spotlight behaviour
				}
			}
		},
		[cloudId, flags, invite, spaceDetail?.name],
	);

	/**
	 * Save-time strategy: traverses the full ADF document to find links not
	 * present in `startingLinks`. Used by comment editors and usePublish, which
	 * already receive the full saved ADF at their natural save point.
	 */
	const handleSave = useCallback(
		(content: ADFEntity) => {
			if (!content) {
				return;
			}
			const classifiedLinks = classifyNewLinks(getAllLinks(content), initialLinksRef.current);
			fireAnalytics(classifiedLinks);
			triggerSpotlight(classifiedLinks.pastedSlackLinks, classifiedLinks.pastedMailtoLinks).catch(
				(_) => {
					//no-op
				},
			);
		},
		[fireAnalytics, triggerSpotlight],
	);

	/**
	 * Paste-time strategy: accepts a pre-extracted list of URLs from only the
	 * pasted slice (no full-document traversal). Used by the live-page editor.
	 */
	const handlePastedLinks = (pastedUrls: string[]) => {
		// Early return if no pasted links or gate is off
		if (pastedUrls.length === 0) {
			return;
		}

		// Get the starting links from the ref
		const startingLinks = initialLinksRef.current;

		const classifiedLinks = classifyNewLinks(pastedUrls, startingLinks);
		fireAnalytics(classifiedLinks);

		triggerSpotlight(classifiedLinks.pastedSlackLinks, classifiedLinks.pastedMailtoLinks).catch(
			(_) => {
				//no-op
			},
		);

		// Add new links to the initial links ref so the flag doesn't spam on duplicate links
		Object.values(classifiedLinks)
			.flat()
			.forEach((link) => initialLinksRef.current.add(link));
	};

	return { handleSave, handlePastedLinks, setInitialLinks };
};
